import { Injectable } from '@angular/core';
import {
  QueryExpressionParserService,
  SimpleChipExpression,
  ExpertChipExpression
} from '@nrwl/query-emitter';
import { ReportGrammar } from './grammars/report.grammar.type';
import { GrammarHelper } from './grammar.helper';
declare var require: any;
const reportGrammarParser = require('./grammars/report.grammar');

@Injectable()
export class ReportsQueryExpressionParserService extends QueryExpressionParserService {
  supportedSearchKeywords: Array<string>;

  constructor() {
    super();
  }

  setSupportedSearchKeywords(keywords) {
    this.supportedSearchKeywords = keywords || [];
  }

  public isChipExpressionValid(chipExpression: string): boolean {
    try {
      const kTree = reportGrammarParser.parse(chipExpression, {
        types: ReportGrammar
      });
      // all explanations are always wrapped as array
      const explanation = kTree.explain();
      let decision = true;
      for (const exp of explanation) {
        decision = decision && GrammarHelper.allExpressionFieldsPopulated(exp);
      }
      return this.areKeywordsValid(explanation) && decision;
    } catch (error) {
      return false; // normally we don't want to print error details
    }
  }

  public parseChipIntoTokens(chipExpression: string): SimpleChipExpression {
    try {
      const kTree = reportGrammarParser.parse(chipExpression, {
        types: ReportGrammar
      });
      // all explanations are always wrapped as array
      const explanation = kTree.explain();
      // just one is accepted. If more is generated - this is an error now.
      if (explanation.length !== 1) {
        throw new Error('Incorrect number of expressions. Expected just one.');
      }
      // normalize value of 'operands' - in case it is not an array, replace value with array.
      return GrammarHelper.normalizeSimpleChipExpression(explanation[0]);
    } catch (error) {
      return null; // normally we don't want to print error details
    }
  }

  public getChipKeywords(): string[] {
    // ideally, we'd need to parse them from grammar file. not sure if list is going to change often, so just
    // hardcode them here.
    return this.supportedSearchKeywords;
  }

  public getChipOperators(): string[] {
    return ['=', '!=', '>', '<', '<=', '>='];
  }

  public isExpressionValid(expression: string): boolean {
    try {
      const kTree = reportGrammarParser.parse(expression, {
        types: ReportGrammar
      });
      // all explanations are always wrapped as array
      const explanation = kTree.explain();
      let valid = true;
      explanation.forEach(subExpression => {
        valid =
          valid && GrammarHelper.allExpressionFieldsPopulated(subExpression);
      });
      return this.areKeywordsValid(explanation) && valid;
    } catch (error) {
      return false; // normally we don't want to print error details
    }
  }

  private areKeywordsValid(chips) {
    return chips.every(this.areChipKeywordValid.bind(this));
  }

  areChipKeywordValid(chip) {
    return this.supportedSearchKeywords.find(
      expl => expl.toLowerCase() === chip.keyword.toLowerCase()
    );
  }

  public fitsWithUseCaseConstraints(chips: SimpleChipExpression[]): boolean {
    // no business logic quirks for forensic
    return true;
  }

  public parseIntoTokens(expression: string): ExpertChipExpression[] {
    try {
      const kTree = reportGrammarParser.parse(expression, {
        types: ReportGrammar
      });
      // all explanations are always wrapped as array
      const explanation = kTree.explain();
      explanation.forEach(token => {
        GrammarHelper.normalizeExpertChipExpression(token);
      });
      return explanation;
    } catch (error) {
      return null;
    }
  }

  toExpression(chips: SimpleChipExpression[]): string {
    return GrammarHelper.toExpression(chips);
  }

  toBreadcrumbExp(chips: SimpleChipExpression[]): string {
    return GrammarHelper.toBreadcrumbExp(chips);
  }

  toAtaBreadcrumbExp(chips: SimpleChipExpression[]): string {
    return GrammarHelper.toAtaBreadcrumbExp(chips);
  }
}
